import type { ColDef } from 'ag-grid-enterprise';

import type {
  AnyReckonGridDefinition,
  ReckonGridColumnIdOf,
} from './reckonGridDefinition';
import type { ReckonGridApi } from './useReckonGrid';

export type BuildReckonGridColumnDefsArgs<
  TDef extends AnyReckonGridDefinition,
  TRow
> = {
  grid: ReckonGridApi<TDef>;
  /** the feature's per-column AG Grid column definitions */
  columnDefs: Record<ReckonGridColumnIdOf<TDef>, ColDef<TRow>>;
  /** unconfigurable columns rendered before the configured ones (e.g. selection) */
  leadingColumns?: Array<ColDef<TRow>>;
  /** unconfigurable columns rendered after the configured ones (e.g. actions) */
  trailingColumns?: Array<ColDef<TRow>>;
};

/**
 * Applies the grid config (visibility, order, resizability, flex-vs-width) to
 * the feature's column definitions. Columns disabled through the grid's
 * isColumnEnabled rule are excluded.
 */
export const buildReckonGridColumnDefs = <
  TDef extends AnyReckonGridDefinition,
  TRow
>(
  args: BuildReckonGridColumnDefsArgs<TDef, TRow>
): Array<ColDef<TRow>> => {
  const reactiveGridConfig = args.grid.reactiveGridConfig;
  const columnIds = (Object.keys(reactiveGridConfig) as Array<
    ReckonGridColumnIdOf<TDef>
  >)
    .filter((columnId) => {
      return (
        reactiveGridConfig[columnId].serverConfig.isVisible &&
        args.grid.isColumnEnabled(columnId)
      );
    })
    .sort((columnAId, columnBId) => {
      return (
        reactiveGridConfig[columnAId].serverConfig.displayOrder -
        reactiveGridConfig[columnBId].serverConfig.displayOrder
      );
    });

  const configuredColumnDefs = columnIds.map((columnId) => {
    const clientConfig = reactiveGridConfig[columnId].clientConfig;
    const customWidth = args.grid.getColumnWidth(columnId);

    return {
      ...args.columnDefs[columnId],
      resizable: clientConfig.resizeDisabled !== true,
      ...(clientConfig.useFlexWidth === true
        ? { width: undefined, flex: customWidth }
        : { flex: undefined, width: customWidth }),
    };
  });

  return [
    ...(args.leadingColumns ?? []),
    ...configuredColumnDefs,
    ...(args.trailingColumns ?? []),
  ];
};
